import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';

import { ApiError } from '@/api/client';
import { useReadiness } from '@/api/health';
import type { DependencyCheck, HealthStatus } from '@/types/api';

const STATUS_META: Record<
  HealthStatus,
  { color: 'success' | 'warning' | 'error'; Icon: typeof CheckCircleIcon }
> = {
  ok: { color: 'success', Icon: CheckCircleIcon },
  degraded: { color: 'warning', Icon: WarningIcon },
  error: { color: 'error', Icon: ErrorIcon },
};

function DependencyRow({ name, check }: { name: string; check: DependencyCheck }) {
  const { color, Icon } = STATUS_META[check.status] ?? STATUS_META.error;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
        <Icon color={color} fontSize="small" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {name}
          </Typography>
          {check.message && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {check.message}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        {check.latency_ms !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {check.latency_ms} ms
          </Typography>
        )}
        <Chip size="small" color={color} variant="outlined" label={check.status} />
      </Stack>
    </Stack>
  );
}

/**
 * Live dependency status.
 *
 * Proves the whole chain end to end: browser → Vite → Laravel → Postgres,
 * Redis, and the ML sidecar. Embedded on the dashboard and served standalone
 * before authentication exists.
 */
export function SystemStatusCard() {
  const { data, isPending, error, isFetching } = useReadiness();

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h3">System status</Typography>
          {isFetching && <CircularProgress size={16} />}
        </Stack>

        {isPending && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <HourglassEmptyIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.secondary">
              Contacting the API…
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" variant="outlined">
            <Typography variant="body2" fontWeight={600}>
              {error instanceof ApiError ? error.title : 'Request failed'}
            </Typography>
            <Typography variant="caption" component="p">
              {error instanceof ApiError && error.detail ? error.detail : String(error)}
            </Typography>
            {/* Only actionable when the API was unreachable — for a 500 the
                stack is running fine and this advice would mislead. */}
            {error instanceof ApiError && error.isNetworkError && (
              <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
                Start the stack with <code>make up</code>.
              </Typography>
            )}
          </Alert>
        )}

        {data && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                size="small"
                color={STATUS_META[data.status]?.color ?? 'error'}
                label={`overall: ${data.status}`}
              />
              <Chip size="small" variant="outlined" label={data.environment} />
              <Chip size="small" variant="outlined" label={`v${data.version}`} />
            </Stack>

            {Object.entries(data.checks).map(([name, check]) => (
              <DependencyRow key={name} name={name} check={check} />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Standalone page, reachable without signing in. */
export function SystemStatusPage() {
  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h1">Quotient AI</Typography>
        <Typography variant="body2" color="text.secondary">
          AI-assisted quotation platform for freight and logistics
        </Typography>
      </Stack>

      <SystemStatusCard />
    </Container>
  );
}
